import { useState, useEffect } from "react";
import s from "./styles.module.scss";
import { useClerkAuth } from "../../hooks/use-clerk-auth";
import apiClient from "../../services/api-client";
import PageHeader from "../../components/page-header";
import { Loading } from "../loading";

const CONSOLE_CACHE_KEY = 'ocean_console_data';
const SUGGESTED_EVENTS_CACHE_KEY = 'ocean_suggested_events';
const SUGGESTIONS_ENABLED_KEY = 'ocean_suggestions_enabled';

const Console = () => {
  const { user, initDone, getSessionToken } = useClerkAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggestionsEnabled, setSuggestionsEnabled] = useState(true);
  const [courses, setCourses] = useState([]);
  const [expandedCourse, setExpandedCourse] = useState(null);

  // Track original values and modifications for undo/save flow
  const [originalCourses, setOriginalCourses] = useState([]);
  const [modifiedCourseIds, setModifiedCourseIds] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Compute if there are unsaved changes
  const hasUnsavedChanges = modifiedCourseIds.size > 0;

  // Initialize console state from data (used by both cache and API paths)
  const initializeConsoleState = (data) => {
    setSuggestionsEnabled(data.suggestions_enabled);
    localStorage.setItem(SUGGESTIONS_ENABLED_KEY, JSON.stringify(data.suggestions_enabled));
    const coursesData = data.courses || [];
    setCourses(coursesData);
    // Store original values for undo functionality
    setOriginalCourses(JSON.parse(JSON.stringify(coursesData)));
    // Clear any pending modifications
    setModifiedCourseIds(new Set());
  };

  // Fetch console data from API (can run in background without showing loading)
  const fetchConsoleFromAPI = async (showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setLoading(true);
      }
      setError(null);

      const response = await apiClient.get("/api/console", {}, getSessionToken);

      if (response.status === "success" && response.data) {
        // Save to localStorage cache
        localStorage.setItem(CONSOLE_CACHE_KEY, JSON.stringify(response.data));
        initializeConsoleState(response.data);
      } else {
        throw new Error(response.message || "Failed to fetch console data");
      }
    } catch (err) {
      console.error("Error fetching console data:", err);
      if (showLoadingState) {
        setError("Failed to load console data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Load console data - check cache first, then refresh from API in background
  const loadConsoleData = async () => {
    // Try to load from localStorage cache first
    const cachedData = localStorage.getItem(CONSOLE_CACHE_KEY);

    if (cachedData) {
      try {
        const data = JSON.parse(cachedData);
        if (data) {
          // Show cached data immediately (no loading screen)
          initializeConsoleState(data);
          setLoading(false);

          // Refresh from API in background
          fetchConsoleFromAPI(false);
          return;
        }
      } catch (e) {
        console.error("Error parsing cached console data:", e);
      }
    }

    // No cache or invalid cache - fetch from API with loading state
    await fetchConsoleFromAPI(true);
  };

  // Load console data on mount
  useEffect(() => {
    if (initDone && user?.uid) {
      loadConsoleData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initDone, user?.uid]);

  const handleToggleSuggestions = async () => {
    try {
      const newState = !suggestionsEnabled;
      setSuggestionsEnabled(newState);
      localStorage.setItem(SUGGESTIONS_ENABLED_KEY, JSON.stringify(newState));

      // Clear suggested events cache immediately when disabling
      if (!newState) {
        localStorage.removeItem(SUGGESTED_EVENTS_CACHE_KEY);
      }

      const response = await apiClient.post(
        "/api/console/toggle",
        { enabled: newState },
        getSessionToken
      );

      if (response.status !== "success") {
        // Revert on failure
        setSuggestionsEnabled(!newState);
        localStorage.setItem(SUGGESTIONS_ENABLED_KEY, JSON.stringify(!newState));
        console.error("Failed to toggle suggestions");
      }
    } catch (err) {
      console.error("Error toggling suggestions:", err);
      setSuggestionsEnabled(!suggestionsEnabled);
      localStorage.setItem(SUGGESTIONS_ENABLED_KEY, JSON.stringify(!suggestionsEnabled));
    }
  };

  // Handle total hours slider change - scale sub-categories proportionally
  const handleTotalHoursChange = (courseId, newTotal) => {
    setCourses(prev => {
      return prev.map(course => {
        if (course.id !== courseId) return course;

        const breakdown = course.breakdown || {};

        // Calculate sum of non-zero sub-categories
        const nonZeroEntries = Object.entries(breakdown).filter(([_, hours]) => hours > 0);
        const currentSum = nonZeroEntries.reduce((sum, [_, h]) => sum + h, 0);

        // If all sub-categories are 0, just update total
        if (currentSum === 0 || !breakdown || Object.keys(breakdown).length === 0) {
          return { ...course, hours_per_week: newTotal };
        }

        // Calculate scale factor
        const scaleFactor = newTotal / currentSum;

        // Scale non-zero sub-categories proportionally (round to nearest 0.1)
        const newBreakdown = { ...breakdown };
        for (const [key, hours] of Object.entries(breakdown)) {
          if (hours > 0) {
            newBreakdown[key] = Math.round(hours * scaleFactor * 10) / 10;
          }
          // Zero stays zero
        }

        return {
          ...course,
          hours_per_week: newTotal,
          breakdown: newBreakdown
        };
      });
    });

    // Mark course as modified
    setModifiedCourseIds(prev => new Set([...prev, courseId]));
  };

  // Handle sub-category slider change - update total to reflect sum
  const handleBreakdownChange = (courseId, eventType, newHours) => {
    setCourses(prev => {
      return prev.map(course => {
        if (course.id !== courseId) return course;

        const newBreakdown = {
          ...course.breakdown,
          [eventType]: newHours
        };

        // Recalculate total as sum of all sub-categories
        const newTotal = Object.values(newBreakdown)
          .reduce((sum, h) => sum + (h || 0), 0);

        return {
          ...course,
          breakdown: newBreakdown,
          hours_per_week: Math.round(newTotal * 10) / 10 // Round to nearest 0.1
        };
      });
    });

    // Mark course as modified
    setModifiedCourseIds(prev => new Set([...prev, courseId]));
  };

  // Undo all changes - revert to original values
  const handleUndoEdits = () => {
    // Restore courses to original values
    setCourses(JSON.parse(JSON.stringify(originalCourses)));

    // Clear modification tracking
    setModifiedCourseIds(new Set());
  };

  // Save all changes - persist to backend and regenerate suggestions
  const handleSaveChanges = async () => {
    if (modifiedCourseIds.size === 0) return;

    setIsSaving(true);

    try {
      // 1. Save all modified course hours to backend
      const courseIds = Array.from(modifiedCourseIds);
      for (const courseId of courseIds) {
        const course = courses.find(c => c.id === courseId);
        if (course) {
          // Save main hours
          await apiClient.patch(
            `/api/console/course/${courseId}/hours`,
            { event_type: null, hours: course.hours_per_week },
            getSessionToken
          );

          // Save breakdown values if they exist
          if (course.breakdown) {
            for (const [key, hours] of Object.entries(course.breakdown)) {
              // Convert breakdown key to singular for API
              const singularKey = key.replace(/s$/, '');
              await apiClient.patch(
                `/api/console/course/${courseId}/hours`,
                { event_type: singularKey, hours },
                getSessionToken
              );
            }
          }
        }
      }

      // 2. Update localStorage cache
      const cachedData = localStorage.getItem(CONSOLE_CACHE_KEY);
      if (cachedData) {
        try {
          const data = JSON.parse(cachedData);
          data.courses = courses;
          localStorage.setItem(CONSOLE_CACHE_KEY, JSON.stringify(data));
        } catch (e) {
          console.error("Error updating console cache:", e);
        }
      }

      // 3. Clear suggested events cache (will be regenerated)
      localStorage.removeItem(SUGGESTED_EVENTS_CACHE_KEY);

      // 4. Trigger regeneration for modified courses only (if suggestions enabled)
      if (suggestionsEnabled) {
        await apiClient.post(
          '/api/console/regenerate-suggestions',
          { course_ids: courseIds },
          getSessionToken
        );
      }

      // 5. Update original values to current (changes are now "saved")
      setOriginalCourses(JSON.parse(JSON.stringify(courses)));

      // 6. Clear modification tracking
      setModifiedCourseIds(new Set());

    } catch (err) {
      console.error("Error saving changes:", err);
      alert("Failed to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleExpanded = (courseId) => {
    setExpandedCourse(expandedCourse === courseId ? null : courseId);
  };

  if (loading) {
    return <Loading text="Loading console" description="Please wait..." />;
  }

  if (error) {
    return (
      <div className={s.consoleContainer}>
        <div className={s.container}>
          <PageHeader title="Console" />
          <div className={s.error}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.consoleContainer} data-tour="console-suggestions">
      <div className={s.container}>
        <div className={s.header}>
          <PageHeader title="Console" />
          <div className={s.toggleContainer}>
            <span className={s.toggleLabel}>Suggested Events</span>
            <button
              className={`${s.toggle} ${suggestionsEnabled ? s.enabled : ""}`}
              onClick={handleToggleSuggestions}
              aria-label="Toggle suggestions"
            >
              <span className={s.toggleSlider} />
            </button>
          </div>
        </div>

        {courses.length === 0 ? (
          <div className={s.noCourses}>
            <p>No courses found. Add courses to see study hour predictions.</p>
          </div>
        ) : (
          <div className={`${s.coursesList} ${!suggestionsEnabled ? s.disabled : ""}`}>
            <div className={s.listHeader}>
              <span className={s.headerLabel}>Course</span>
              <span className={s.headerLabel}>Hours / week</span>
            </div>

            {courses.map((course) => (
              <div key={course.id} className={s.courseRow}>
                <div
                  className={s.courseMain}
                  onClick={() => suggestionsEnabled && toggleExpanded(course.id)}
                >
                  <div className={s.courseInfo}>
                    <span
                      className={s.colorDot}
                      style={{ backgroundColor: course.color || "#039be5" }}
                    />
                    <span className={s.courseName}>{course.name}</span>
                  </div>
                  <div className={s.sliderContainer} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="range"
                      min="0"
                      max="12"
                      step="0.5"
                      value={course.hours_per_week || 0}
                      onChange={(e) =>
                        suggestionsEnabled &&
                        handleTotalHoursChange(course.id, parseFloat(e.target.value))
                      }
                      disabled={!suggestionsEnabled}
                      className={s.slider}
                      style={{
                        "--slider-color": course.color || "#039be5",
                        "--slider-percent": `${((course.hours_per_week || 0) / 12) * 100}%`
                      }}
                    />
                    <span className={s.hoursValue}>
                      {(course.hours_per_week || 0).toFixed(1)}
                    </span>
                  </div>
                  <span className={`${s.expandIcon} ${expandedCourse === course.id ? s.expanded : ""}`}>
                    {suggestionsEnabled ? "▼" : ""}
                  </span>
                </div>

                {expandedCourse === course.id && suggestionsEnabled && (
                  <div className={s.breakdown}>
                    <div className={s.breakdownHeader}>Hours / item</div>
                    {[
                      { key: "assignments", label: "Assignments" },
                      { key: "quizzes", label: "Quizzes" },
                      { key: "exams", label: "Exams" },
                      { key: "essays", label: "Essays" }
                    ].map(({ key, label }) => (
                      <div key={key} className={s.breakdownRow}>
                        <span className={s.breakdownLabel}>{label}</span>
                        <div className={s.sliderContainer}>
                          <input
                            type="range"
                            min="0"
                            max="7"
                            step="0.5"
                            value={course.breakdown?.[key] || 0}
                            onChange={(e) =>
                              handleBreakdownChange(course.id, key, parseFloat(e.target.value))
                            }
                            className={s.slider}
                            style={{
                              "--slider-color": course.color || "#039be5",
                              "--slider-percent": `${((course.breakdown?.[key] || 0) / 7) * 100}%`
                            }}
                          />
                          <span className={s.hoursValue}>
                            {(course.breakdown?.[key] || 0).toFixed(1)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Action buttons - only show when there are unsaved changes */}
        {hasUnsavedChanges && (
          <div className={s.actionButtons}>
            <button
              className={s.undoButton}
              onClick={handleUndoEdits}
              disabled={isSaving}
            >
              Undo edits
            </button>
            <button
              className={s.saveButton}
              onClick={handleSaveChanges}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Console;
